import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  useLayoutEffect,
} from 'react'

import { Sections } from './Sections/Sections'
import Seats from './Seats/Seats'
import useSeatmapStore from '../../../../../../../stores/seatmapStore'
import useFiltersStore from '../../../../../../../stores/filtersStore'
import { useTooltips } from '../providers/TooltipProvider'
import { useActions } from '../providers/ActionsProvider'
import { Spinner } from '../../../../../../LoadingContainer/Spinner'
import { isSectionCoverVenue } from './sectionCoverVenues'
import {
  intersectionArea,
  pointInRect,
  rectCenterDistanceSq,
  rectanglesIntersect,
} from './svgSectionCoverGeometry'
import {
  countSectionAvailability,
  isSectionSoldOut,
} from './Sections/sectionAvailability'

const PAN_THRESHOLD_PX = 5

/** Same ZoomLevel % formula as `calculateScalePercentage`; past this, show seats without a section click. */
const SEAT_REVEAL_MIN_PERCENT = 22

function zoomableBoundsEqual(a, b) {
  if (!a || !b) return false
  const q = (n) => Math.round(n * 100) / 100
  return (
    q(a.x) === q(b.x) &&
    q(a.y) === q(b.y) &&
    q(a.width) === q(b.width) &&
    q(a.height) === q(b.height)
  )
}

function unionBoundingRects(rects) {
  const valid = rects.filter(
    (r) =>
      r &&
      Number.isFinite(r.x) &&
      Number.isFinite(r.y) &&
      Number.isFinite(r.width) &&
      Number.isFinite(r.height) &&
      r.width > 0 &&
      r.height > 0
  )
  if (valid.length === 0) return null
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const r of valid) {
    minX = Math.min(minX, r.x)
    minY = Math.min(minY, r.y)
    maxX = Math.max(maxX, r.x + r.width)
    maxY = Math.max(maxY, r.y + r.height)
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
}

function resolveSeatmapSection(sections, id) {
  return (
    sections[id] ??
    sections[String(id)] ??
    (typeof id === 'string' && /^\d+$/.test(id)
      ? sections[Number(id)]
      : undefined)
  )
}

/**
 * Single-letter blocks (Q, X, …) and `WC` + same letter (WCQ, WCX, …) share a
 * seam; the WC shard is often outside the neighbor rect or rows live on only
 * one id. When either side is in seat view, include both zoomable section ids.
 * Skips double letters (QQ, WC is not /^WC[A-Z]$/).
 */
function expandWcSeamPairSectionIds(sectionIds, sections) {
  if (!sections || !Array.isArray(sectionIds) || sectionIds.length === 0) {
    return sectionIds
  }
  const set = new Set(sectionIds.map((id) => String(id)))
  const letters = new Set()
  for (const id of set) {
    const s = resolveSeatmapSection(sections, id)
    if (!s?.sectionId) continue
    const raw = String(s.sectionNumber ?? '')
      .trim()
      .toUpperCase()
    const compact = raw.replace(/\s+/g, '')
    if (/^[A-Z]$/.test(raw)) letters.add(raw)
    const m = compact.match(/^WC([A-Z])$/)
    if (m) letters.add(m[1])
  }
  if (letters.size === 0) return sectionIds

  for (const s of Object.values(sections)) {
    if (!s?.sectionId) continue
    const raw = String(s.sectionNumber ?? '')
      .trim()
      .toUpperCase()
    const compact = raw.replace(/\s+/g, '')
    const sid = String(s.sectionId)
    for (const L of letters) {
      if (raw === L || compact === `WC${L}`) {
        set.add(sid)
        break
      }
    }
  }
  return Array.from(set)
}

function runViewportAnimation(from, to, durationMs, onFrame, onFinish, rafRef) {
  if (rafRef.current) {
    cancelAnimationFrame(rafRef.current)
  }
  const start = performance.now()
  const tick = (now) => {
    const t = Math.min(1, (now - start) / durationMs)
    const k = t * (2 - t)
    onFrame({
      scale: from.scale + (to.scale - from.scale) * k,
      posX: from.posX + (to.posX - from.posX) * k,
      posY: from.posY + (to.posY - from.posY) * k,
    })
    if (t < 1) {
      rafRef.current = requestAnimationFrame(tick)
    } else {
      rafRef.current = null
      onFinish?.()
    }
  }
  rafRef.current = requestAnimationFrame(tick)
}

export default function SvgSeatmap({ loadingComplete, mapError }) {
  const data = useSeatmapStore((state) => state.data)
  const seatLookupTable = useSeatmapStore((state) => state.seatLookupTable)
  const setScale = useSeatmapStore((state) => state.setScale)
  const background = useSeatmapStore((state) => state.background)
  const { hideTooltip } = useTooltips()
  const { stageRef, originalScaleRef, SCALE_FACTOR, SCALE_LIMITS } =
    useActions()

  const venueSlug = useFiltersStore((s) => s.event?.venue?.slug)
  const sectionCoversEnabled = isSectionCoverVenue(venueSlug)

  const originalWidth = background?.width
  const originalHeight = background?.height
  const aspectRatio = originalWidth / originalHeight

  const [width, setWidth] = useState(0)
  const [height, setHeight] = useState(0)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [viewport, setViewport] = useState({ scale: 1, posX: 0, posY: 0 })

  const [isInteracting, setIsInteracting] = useState(false)
  const [isPanPinching, setIsPanPinching] = useState(false)
  const THROTTLE_INTERVAL = 16
  const MAX_WHEEL_DELTA = 100
  const interactionTimeout = useRef(null)
  const wheelTimeout = useRef(null)
  const accumulatedDelta = useRef(0)
  const lastWheelTime = useRef(0)
  const isZooming = useRef(false)

  const isPanPinchingRef = useRef(false)
  isPanPinchingRef.current = isPanPinching

  const pinchRef = useRef({ lastCenter: null, lastDist: 0 })
  const dragStoppedRef = useRef(false)
  const transitionTouchInfo = useRef(null)

  const [focusedSection, setFocusedSection] = useState(null)
  const [boundsGeneration, setBoundsGeneration] = useState(0)
  const [activeRowIds, setActiveRowIds] = useState(null)
  const [viewportSectionId, setViewportSectionId] = useState(null)
  const [clickedSectionId, setClickedSectionId] = useState(null)
  const [lockedViewSectionId, setLockedViewSectionId] = useState(null)
  const [isTransitioning, setIsTransitioning] = useState(false)

  const zoomableSectionBoundsRef = useRef({})
  const defaultViewRef = useRef(null)
  const previousViewRef = useRef(null)
  const isAnimatingToSectionRef = useRef(false)
  const dimensionsRef = useRef({ width: 0, height: 0 })
  const offsetSyncRef = useRef({ x: 0, y: 0 })

  const svgRef = useRef(null)
  const zoomAnimRafRef = useRef(null)
  const seatmapScaleStoreRafRef = useRef(null)
  const pendingSeatmapScaleRef = useRef(null)
  const panRef = useRef({
    candidate: false,
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    origPosX: 0,
    origPosY: 0,
  })

  const viewportRef = useRef(viewport)
  viewportRef.current = viewport
  dimensionsRef.current = { width, height }
  offsetSyncRef.current = offset

  const stateForStageRef = useRef({
    scale: viewport.scale,
    posX: viewport.posX,
    posY: viewport.posY,
    width,
    height,
    offsetX: offset.x,
    offsetY: offset.y,
  })
  stateForStageRef.current = {
    scale: viewport.scale,
    posX: viewport.posX,
    posY: viewport.posY,
    width,
    height,
    offsetX: offset.x,
    offsetY: offset.y,
  }

  const calculateScalePercentage = useCallback(
    (scaleToUse) => {
      const ratio = scaleToUse / originalScaleRef.current
      const percentage = (ratio * 100 - 100) / 10
      return Number(percentage.toFixed(0))
    },
    [originalScaleRef]
  )

  /** Coalesces Zustand scale updates during pinch/wheel so ZoomLevel does not force extra React passes. */
  const scheduleSeatmapScaleToStore = useCallback(
    (nextScale) => {
      pendingSeatmapScaleRef.current = nextScale
      if (seatmapScaleStoreRafRef.current != null) return
      seatmapScaleStoreRafRef.current = requestAnimationFrame(() => {
        seatmapScaleStoreRafRef.current = null
        const s = pendingSeatmapScaleRef.current
        if (s != null) setScale(s)
      })
    },
    [setScale]
  )

  const setZoomableSectionBounds = useCallback((sectionId, rect) => {
    if (!sectionId || !rect) return
    const key = String(sectionId)
    const prev = zoomableSectionBoundsRef.current[key]
    if (prev && zoomableBoundsEqual(prev, rect)) {
      return
    }
    zoomableSectionBoundsRef.current = {
      ...zoomableSectionBoundsRef.current,
      [key]: rect,
    }
    setBoundsGeneration((g) => g + 1)
  }, [])

  const getViewportRectInContentCoords = useCallback(() => {
    const s = viewportRef.current.scale
    const posX = viewportRef.current.posX
    const posY = viewportRef.current.posY
    const ox = offsetSyncRef.current.x
    const oy = offsetSyncRef.current.y
    const w = dimensionsRef.current.width
    const h = dimensionsRef.current.height
    if (!w || !h) return null

    const left = (0 - posX) / s + ox
    const top = (0 - posY) / s + oy
    const right = (w - posX) / s + ox
    const bottom = (h - posY) / s + oy

    return {
      x: Math.min(left, right),
      y: Math.min(top, bottom),
      width: Math.abs(right - left),
      height: Math.abs(bottom - top),
    }
  }, [])

  const pickZoomableSectionInViewport = useCallback(() => {
    const viewport = getViewportRectInContentCoords()
    if (!viewport) return null

    const boundsMap = zoomableSectionBoundsRef.current || {}
    const ids = Object.keys(boundsMap)
    if (ids.length === 0) return null

    const viewportCenter = {
      x: viewport.x + viewport.width / 2,
      y: viewport.y + viewport.height / 2,
    }

    const containingCenter = ids.filter((id) =>
      pointInRect(viewportCenter, boundsMap[id])
    )
    if (containingCenter.length > 0) {
      let bestCenterId = null
      let bestCenterScore = -1
      for (const id of containingCenter) {
        const r = boundsMap[id]
        const area = intersectionArea(viewport, r)
        const sectionArea = Math.max(1, r.width * r.height)
        const score = area / sectionArea
        if (score > bestCenterScore) {
          bestCenterScore = score
          bestCenterId = id
        }
      }
      if (bestCenterId) return bestCenterId
    }

    let bestId = null
    let bestScore = -1
    let bestArea = 0
    for (const id of ids) {
      const r = boundsMap[id]
      if (!r) continue
      const area = intersectionArea(viewport, r)
      if (area <= 0) continue
      const sectionArea = Math.max(1, r.width * r.height)
      const score = area / sectionArea
      if (score > bestScore || (score === bestScore && area > bestArea)) {
        bestScore = score
        bestArea = area
        bestId = id
      }
    }
    if (bestId) return bestId

    let nearestId = null
    let nearestDist = Number.POSITIVE_INFINITY
    for (const id of ids) {
      const r = boundsMap[id]
      if (!r) continue
      const d2 = rectCenterDistanceSq(viewport, r)
      if (d2 < nearestDist) {
        nearestDist = d2
        nearestId = id
      }
    }
    return nearestId
  }, [getViewportRectInContentCoords])

  const getNeighborZoomableSectionIds = useCallback(
    (clickedSectionId, clickedRect) => {
      if (!clickedSectionId || !clickedRect) return []
      const margin = Math.max(
        72,
        Math.min(clickedRect.width, clickedRect.height) * 0.55
      )
      const expanded = {
        x: clickedRect.x - margin,
        y: clickedRect.y - margin,
        width: clickedRect.width + margin * 2,
        height: clickedRect.height + margin * 2,
      }

      const boundsMap = zoomableSectionBoundsRef.current || {}
      const ids = Object.keys(boundsMap).filter((id) => id !== clickedSectionId)
      const neighbors = ids.filter((id) =>
        rectanglesIntersect(expanded, boundsMap[id])
      )
      if (neighbors.length > 0) return neighbors.slice(0, 32)

      const cx = clickedRect.x + clickedRect.width / 2
      const cy = clickedRect.y + clickedRect.height / 2
      return ids
        .map((id) => {
          const r = boundsMap[id]
          const rcx = r.x + r.width / 2
          const rcy = r.y + r.height / 2
          return { id, d2: (rcx - cx) ** 2 + (rcy - cy) ** 2 }
        })
        .sort((a, b) => a.d2 - b.d2)
        .slice(0, 16)
        .map((x) => x.id)
    },
    []
  )

  const getRowsForZoomableSectionIds = useCallback(
    (sectionIds) => {
      if (!Array.isArray(sectionIds) || sectionIds.length === 0) return []
      return sectionIds.flatMap((id) => {
        const sections = data?.sections
        if (!sections) return []
        const s =
          sections[id] ??
          sections[String(id)] ??
          (typeof id === 'string' && /^\d+$/.test(id)
            ? sections[Number(id)]
            : undefined)
        if (!s || s.hidden) return []
        const stats = countSectionAvailability(s, data?.rows, seatLookupTable)
        if (isSectionSoldOut(stats)) return []
        return Array.isArray(s?.rows) ? s.rows : []
      })
    },
    [data?.rows, data?.sections, seatLookupTable]
  )

  const zoomToSection = useCallback(
    (section, sectionRect, rowIdsOverride = null, onFinish = null) => {
      const w = dimensionsRef.current.width
      const h = dimensionsRef.current.height
      if (!section || !sectionRect || !w || !h) return

      const v = viewportRef.current
      if (!previousViewRef.current) {
        previousViewRef.current = {
          scale: v.scale,
          posX: v.posX,
          posY: v.posY,
        }
      }

      hideTooltip?.()

      const padding = 0.86
      const scaleX = w / Math.max(1, sectionRect.width)
      const scaleY = h / Math.max(1, sectionRect.height)
      let newScale = Math.min(scaleX, scaleY) * padding

      const wouldBeScale = calculateScalePercentage(newScale)
      if (wouldBeScale > SCALE_LIMITS.MAX) {
        const maxRatio = (SCALE_LIMITS.MAX * 10 + 100) / 100
        newScale = originalScaleRef.current * maxRatio
      } else if (wouldBeScale < SCALE_LIMITS.MIN) {
        const minRatio = (SCALE_LIMITS.MIN * 10 + 100) / 100
        newScale = originalScaleRef.current * minRatio
      }

      const centerX = w / 2
      const centerY = h / 2
      const target = {
        x: sectionRect.x + sectionRect.width / 2,
        y: sectionRect.y + sectionRect.height / 2,
      }
      const ox = offsetSyncRef.current.x
      const oy = offsetSyncRef.current.y

      const newPos = {
        x: centerX - (target.x - ox) * newScale,
        y: centerY - (target.y - oy) * newScale,
      }

      setIsTransitioning(true)
      isAnimatingToSectionRef.current = true

      if (Array.isArray(rowIdsOverride)) {
        setActiveRowIds(rowIdsOverride)
      } else {
        setActiveRowIds(Array.isArray(section.rows) ? section.rows : [])
      }

      const stage = stageRef.current
      if (stage?.to) {
        stage.to({
          scaleX: newScale,
          scaleY: newScale,
          x: newPos.x,
          y: newPos.y,
          duration: 0.32,
          onFinish: () => {
            isAnimatingToSectionRef.current = false
            setFocusedSection(section)
            setIsTransitioning(false)
            onFinish?.()
          },
        })
      }
    },
    [
      SCALE_LIMITS.MAX,
      SCALE_LIMITS.MIN,
      calculateScalePercentage,
      hideTooltip,
      originalScaleRef,
      stageRef,
    ]
  )

  const resetSectionFocus = useCallback(() => {
    hideTooltip?.()
    const nextView = defaultViewRef.current ?? previousViewRef.current
    previousViewRef.current = null
    if (!nextView) return

    setIsTransitioning(true)
    isAnimatingToSectionRef.current = true
    const stage = stageRef.current
    if (stage?.to) {
      stage.to({
        scaleX: nextView.scale,
        scaleY: nextView.scale,
        x: nextView.posX ?? nextView.x ?? 0,
        y: nextView.posY ?? nextView.y ?? 0,
        duration: 0.28,
        onFinish: () => {
          isAnimatingToSectionRef.current = false
          setFocusedSection(null)
          setActiveRowIds(null)
          setClickedSectionId(null)
          setLockedViewSectionId(null)
          setViewportSectionId(null)
          setIsTransitioning(false)
        },
      })
    }
  }, [hideTooltip, stageRef])

  const onSectionClick = useCallback(
    (e, section, rect) => {
      if (!sectionCoversEnabled || !section?.zoomable || section.hidden) return
      if (isAnimatingToSectionRef.current) return
      const clickedStats = countSectionAvailability(
        section,
        data?.rows,
        seatLookupTable
      )
      if (isSectionSoldOut(clickedStats)) return
      const neighborIds = getNeighborZoomableSectionIds(section.sectionId, rect)
      const rowScopeIds = expandWcSeamPairSectionIds(
        [String(section.sectionId), ...neighborIds],
        data?.sections
      )
      const boundsMap = zoomableSectionBoundsRef.current || {}
      const rectsForZoom = [rect]
      for (const id of neighborIds) {
        const r = boundsMap[id]
        if (r) rectsForZoom.push(r)
      }
      for (const id of rowScopeIds) {
        const r = boundsMap[String(id)]
        if (r) rectsForZoom.push(r)
      }
      const zoomRect = unionBoundingRects(rectsForZoom) ?? rect
      const combined = Array.from(
        new Set([
          ...(Array.isArray(section.rows) ? section.rows : []),
          ...getRowsForZoomableSectionIds(rowScopeIds),
        ])
      )
      setClickedSectionId(section.sectionId)
      setLockedViewSectionId(section.sectionId)
      zoomToSection(section, zoomRect, combined, () => {
        setViewportSectionId(section.sectionId)
      })
    },
    [
      data?.sections,
      data?.rows,
      seatLookupTable,
      sectionCoversEnabled,
      getNeighborZoomableSectionIds,
      getRowsForZoomableSectionIds,
      zoomToSection,
    ]
  )

  useEffect(() => {
    const resize = () => {
      const map = document.getElementById('map')
      if (!map) return

      const containerWidth = map.clientWidth
      const containerHeight = map.clientHeight
      const maxByWidth = containerWidth
      const maxByHeight = containerHeight * aspectRatio

      let newWidth
      let newHeight
      if (maxByWidth < maxByHeight) {
        newWidth = containerWidth
        newHeight = containerWidth / aspectRatio
      } else {
        newWidth = containerHeight * aspectRatio
        newHeight = containerHeight
      }

      const scaleX = newWidth / originalWidth
      const scaleY = newHeight / originalHeight
      const scale = Math.min(scaleX, scaleY) * 0.8
      originalScaleRef.current = scale

      setViewport({ scale, posX: 0, posY: 0 })
      setScale(scale)

      setWidth(containerWidth)
      setHeight(containerHeight)

      const adjustedOffsetX = (originalWidth - containerWidth / scale) / 2
      const adjustedOffsetY = (originalHeight - containerHeight / scale) / 2
      setOffset({ x: adjustedOffsetX, y: adjustedOffsetY })
      defaultViewRef.current = { scale, posX: 0, posY: 0 }
    }

    if (loadingComplete) {
      resize()
      window.addEventListener('resize', resize)
      return () => window.removeEventListener('resize', resize)
    }
  }, [
    aspectRatio,
    originalHeight,
    originalWidth,
    originalScaleRef,
    setScale,
    loadingComplete,
  ])

  const resetInteractionState = () => {
    if (interactionTimeout.current) {
      clearTimeout(interactionTimeout.current)
    }
    interactionTimeout.current = setTimeout(() => {
      setIsInteracting(false)
      setIsPanPinching(false)
    }, 150)
  }

  useEffect(() => {
    if (isInteracting) {
      hideTooltip()
    }
  }, [isInteracting, hideTooltip])

  useLayoutEffect(() => {
    return () => {
      if (zoomAnimRafRef.current) {
        cancelAnimationFrame(zoomAnimRafRef.current)
      }
    }
  }, [])

  useLayoutEffect(() => {
    stageRef.current = {
      scaleX: () => stateForStageRef.current.scale,
      x: () => stateForStageRef.current.posX,
      y: () => stateForStageRef.current.posY,
      width: () => stateForStageRef.current.width,
      height: () => stateForStageRef.current.height,
      setAttrs: (attrs) => {
        if (attrs.scaleX != null) {
          const s = attrs.scaleX
          setViewport((v) => ({ ...v, scale: s }))
          setScale(s)
        }
        if (attrs.x != null || attrs.y != null) {
          setViewport((v) => ({
            ...v,
            posX: attrs.x ?? v.posX,
            posY: attrs.y ?? v.posY,
          }))
        }
      },
      batchDraw: () => {},
      isDragging: () => panRef.current.active,
      startDrag: () => {
        panRef.current.active = true
      },
      stopDrag: () => {
        panRef.current.active = false
        panRef.current.pointerId = null
        panRef.current.candidate = false
      },
      getPointerPosition: () => ({
        x: stateForStageRef.current.width / 2,
        y: stateForStageRef.current.height / 2,
      }),
      to: (config) => {
        const from = {
          scale: viewportRef.current.scale,
          posX: viewportRef.current.posX,
          posY: viewportRef.current.posY,
        }
        const to = {
          scale: config.scaleX,
          posX: config.x,
          posY: config.y,
        }
        const durationMs = (config.duration ?? 0.18) * 1000
        runViewportAnimation(
          from,
          to,
          durationMs,
          ({ scale, posX, posY }) => {
            setViewport({ scale, posX, posY })
            setScale(scale)
          },
          config.onFinish,
          zoomAnimRafRef
        )
      },
    }
    return () => {
      stageRef.current = null
    }
  }, [setScale, stageRef])

  const pointerInSvg = (clientX, clientY) => {
    const svg = svgRef.current
    if (!svg) return { x: 0, y: 0 }
    const rect = svg.getBoundingClientRect()
    return { x: clientX - rect.left, y: clientY - rect.top }
  }

  const isSeatTarget = (target) =>
    Boolean(target?.closest?.('[data-interactive-seat]'))

  const handlePointerDown = (e) => {
    if (e.button !== 0) return
    if (isPanPinchingRef.current) return
    if (isSeatTarget(e.target)) return

    panRef.current = {
      candidate: true,
      active: false,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origPosX: viewportRef.current.posX,
      origPosY: viewportRef.current.posY,
    }
  }

  const handlePointerMove = (e) => {
    const p = panRef.current
    if (p.pointerId !== e.pointerId) return
    if (isPanPinchingRef.current) return

    if (p.candidate && !p.active) {
      const d = Math.hypot(e.clientX - p.startX, e.clientY - p.startY)
      if (d > PAN_THRESHOLD_PX) {
        p.candidate = false
        p.active = true
        setIsInteracting(true)
        if (sectionCoversEnabled) {
          setLockedViewSectionId(null)
        }
        const svg = svgRef.current
        if (svg) {
          svg.setPointerCapture(e.pointerId)
        }
      } else {
        return
      }
    }

    if (!p.active) return

    const dx = e.clientX - p.startX
    const dy = e.clientY - p.startY
    setViewport((v) => ({
      ...v,
      posX: p.origPosX + dx,
      posY: p.origPosY + dy,
    }))
  }

  const releasePanPointer = (e) => {
    const p = panRef.current
    if (p.pointerId !== e.pointerId) return
    if (p.active) {
      const svg = svgRef.current
      try {
        if (svg) svg.releasePointerCapture(e.pointerId)
      } catch {
        /* ignore */
      }
      resetInteractionState()
    }
    panRef.current = {
      candidate: false,
      active: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      origPosX: 0,
      origPosY: 0,
    }
  }

  const handlePointerUp = (e) => {
    releasePanPointer(e)
  }

  const getDistance = (p1, p2) =>
    Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2))

  const getCenter = (p1, p2) => ({
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
  })

  const nativeListenersRef = useRef({
    onWheel: () => {},
    onTouchMove: () => {},
    onTouchEnd: () => {},
  })

  nativeListenersRef.current.onWheel = (e) => {
    e.preventDefault()
    setIsInteracting(true)

    const rawDelta = e.deltaY !== 0 ? e.deltaY : e.deltaX !== 0 ? e.deltaX : 0
    accumulatedDelta.current += Math.min(
      Math.max(rawDelta, -MAX_WHEEL_DELTA),
      MAX_WHEEL_DELTA
    )

    const now = performance.now()
    if (
      !isZooming.current &&
      now - lastWheelTime.current >= THROTTLE_INTERVAL
    ) {
      isZooming.current = true

      wheelTimeout.current = requestAnimationFrame(() => {
        const oldScale = viewportRef.current.scale
        const pointer = pointerInSvg(e.clientX, e.clientY)

        const direction = accumulatedDelta.current > 0 ? -1 : 1
        const zoomIntensity = Math.min(
          Math.abs(accumulatedDelta.current) / 35,
          1
        )
        const scaleBy = 1 + (SCALE_FACTOR - 1) * zoomIntensity

        const posX = viewportRef.current.posX
        const posY = viewportRef.current.posY
        const mousePointTo = {
          x: (pointer.x - posX) / oldScale,
          y: (pointer.y - posY) / oldScale,
        }

        const newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy

        const wouldBeScale = calculateScalePercentage(newScale)
        if (
          wouldBeScale > SCALE_LIMITS.MAX ||
          wouldBeScale < SCALE_LIMITS.MIN
        ) {
          if (wheelTimeout.current) {
            cancelAnimationFrame(wheelTimeout.current)
          }
          wheelTimeout.current = null
          isZooming.current = false
          accumulatedDelta.current = 0
          resetInteractionState()
          return
        }

        const newPos = {
          x: pointer.x - mousePointTo.x * newScale,
          y: pointer.y - mousePointTo.y * newScale,
        }

        setViewport({
          scale: newScale,
          posX: newPos.x,
          posY: newPos.y,
        })
        scheduleSeatmapScaleToStore(newScale)

        wheelTimeout.current = null
        isZooming.current = false
        lastWheelTime.current = now
        accumulatedDelta.current = 0

        resetInteractionState()
      })
    }
  }

  nativeListenersRef.current.onTouchMove = (e) => {
    e.preventDefault()
    setIsInteracting(true)

    const touches = e.touches
    const touch1 = touches[0]
    const touch2 = touches[1]

    const v = viewportRef.current

    if (
      touches.length === 1 &&
      dragStoppedRef.current &&
      transitionTouchInfo.current
    ) {
      const currentTouch = touches[0]
      const { stagePosOnLift, touchPosOnLift } = transitionTouchInfo.current

      const dx = currentTouch.clientX - touchPosOnLift.x
      const dy = currentTouch.clientY - touchPosOnLift.y

      const newPos = {
        x: stagePosOnLift.x + dx,
        y: stagePosOnLift.y + dy,
      }

      setViewport((vp) => ({ ...vp, posX: newPos.x, posY: newPos.y }))

      transitionTouchInfo.current = null
      dragStoppedRef.current = false
      panRef.current = {
        candidate: false,
        active: true,
        pointerId: currentTouch.identifier,
        startX: currentTouch.clientX,
        startY: currentTouch.clientY,
        origPosX: newPos.x,
        origPosY: newPos.y,
      }
      return
    }

    if (touch1 && touch2) {
      setIsPanPinching(true)
      if (panRef.current.active || panRef.current.candidate) {
        panRef.current = {
          candidate: false,
          active: false,
          pointerId: null,
          startX: 0,
          startY: 0,
          origPosX: 0,
          origPosY: 0,
        }
        dragStoppedRef.current = true
      }

      const p1 = { x: touch1.clientX, y: touch1.clientY }
      const p2 = { x: touch2.clientX, y: touch2.clientY }

      const newCenter = getCenter(p1, p2)
      const dist = getDistance(p1, p2)

      const { lastCenter } = pinchRef.current
      if (lastCenter == null) {
        pinchRef.current = { lastCenter: newCenter, lastDist: dist }
        return
      }
      const lastDist = pinchRef.current.lastDist

      const oldScale = v.scale
      const pointTo = {
        x: (lastCenter.x - v.posX) / oldScale,
        y: (lastCenter.y - v.posY) / oldScale,
      }

      const ZOOM_DAMPING_FACTOR = 0.7
      const rawZoomRatio = dist / lastDist
      const effectiveZoomRatio = 1 + (rawZoomRatio - 1) * ZOOM_DAMPING_FACTOR
      const scale = oldScale * effectiveZoomRatio

      const wouldBeScale = calculateScalePercentage(scale)

      if (wouldBeScale > SCALE_LIMITS.MAX || wouldBeScale < SCALE_LIMITS.MIN) {
        pinchRef.current = { lastCenter: newCenter, lastDist: dist }
        return
      }

      const dx = newCenter.x - lastCenter.x
      const dy = newCenter.y - lastCenter.y

      const newPos = {
        x: newCenter.x - pointTo.x * scale + dx,
        y: newCenter.y - pointTo.y * scale + dy,
      }

      setViewport({
        scale,
        posX: newPos.x,
        posY: newPos.y,
      })
      scheduleSeatmapScaleToStore(scale)

      pinchRef.current = { lastCenter: newCenter, lastDist: dist }
    }
  }

  nativeListenersRef.current.onTouchEnd = (e) => {
    const touches = e.touches

    if (touches.length === 1 && dragStoppedRef.current) {
      const remainingTouch = touches[0]
      const v = viewportRef.current
      transitionTouchInfo.current = {
        stagePosOnLift: { x: v.posX, y: v.posY },
        touchPosOnLift: {
          x: remainingTouch.clientX,
          y: remainingTouch.clientY,
        },
      }
    } else {
      pinchRef.current = { lastCenter: null, lastDist: 0 }
      dragStoppedRef.current = false
      transitionTouchInfo.current = null
      setIsPanPinching(false)

      if (touches.length === 0) {
        panRef.current = {
          candidate: false,
          active: false,
          pointerId: null,
          startX: 0,
          startY: 0,
          origPosX: 0,
          origPosY: 0,
        }
      }
    }

    resetInteractionState()
  }

  useEffect(() => {
    const svg = svgRef.current
    if (!loadingComplete) return

    const opts = { passive: false }
    const onTouchMoveNative = (ev) => nativeListenersRef.current.onTouchMove(ev)
    const onTouchEndNative = (ev) => nativeListenersRef.current.onTouchEnd(ev)

    if (svg) {
      svg.addEventListener('touchmove', onTouchMoveNative, opts)
      svg.addEventListener('touchend', onTouchEndNative)
      svg.addEventListener('touchcancel', onTouchEndNative)
    }

    /** Wheel on nested SVG shapes (seat rects) does not always reach a listener on `<svg>`; use window capture. */
    const mapEl = () => document.getElementById('map')
    /** Overlays live inside #map but own their wheel (their own scrolling, or none). */
    const WHEEL_OVERLAY_SELECTOR =
      '.zoomContainer, .vjx-legend, .mapsheet, .vjx-tooltipWrapper'
    const shouldZoomMapFromWheel = (ev) => {
      const root = mapEl()
      if (!root?.contains(ev.target)) return false
      const t = ev.target
      if (t?.closest?.('#map button')) return false
      if (t?.closest?.(WHEEL_OVERLAY_SELECTOR)) return false
      return true
    }
    const onWheelWindow = (ev) => {
      if (!shouldZoomMapFromWheel(ev)) return
      nativeListenersRef.current.onWheel(ev)
    }
    const wheelOpts = { ...opts, capture: true }
    window.addEventListener('wheel', onWheelWindow, wheelOpts)

    return () => {
      window.removeEventListener('wheel', onWheelWindow, wheelOpts)
      if (svg) {
        svg.removeEventListener('touchmove', onTouchMoveNative)
        svg.removeEventListener('touchend', onTouchEndNative)
        svg.removeEventListener('touchcancel', onTouchEndNative)
      }
    }
  }, [loadingComplete])

  useEffect(() => {
    return () => {
      if (wheelTimeout.current) {
        cancelAnimationFrame(wheelTimeout.current)
      }
      if (seatmapScaleStoreRafRef.current) {
        cancelAnimationFrame(seatmapScaleStoreRafRef.current)
        seatmapScaleStoreRafRef.current = null
      }
      accumulatedDelta.current = 0
      isZooming.current = false
      lastWheelTime.current = 0
      transitionTouchInfo.current = null
    }
  }, [])

  const seatsVisible =
    sectionCoversEnabled && Boolean(focusedSection) && !isTransitioning

  const zoomRevealSeats =
    sectionCoversEnabled &&
    !focusedSection &&
    !isTransitioning &&
    calculateScalePercentage(viewport.scale) >= SEAT_REVEAL_MIN_PERCENT

  /**
   * Same row pipeline as click-to-zoom: viewport anchor → geometric neighbors → WC pairs → rows.
   * Anchor is `pickZoomableSectionInViewport` (same idea as `viewportSectionId` after a click).
   */
  const zoomRevealSeatViewZoomableSectionIds = useMemo(() => {
    if (!zoomRevealSeats) return []
    const anchorId = pickZoomableSectionInViewport()
    if (anchorId == null || anchorId === '') return []
    const rect = zoomableSectionBoundsRef.current?.[String(anchorId)]
    if (!rect) return []
    const neighbors = getNeighborZoomableSectionIds(anchorId, rect)
    return Array.from(new Set([String(anchorId), ...neighbors]))
  }, [
    zoomRevealSeats,
    boundsGeneration,
    viewport.scale,
    viewport.posX,
    viewport.posY,
    width,
    height,
    offset.x,
    offset.y,
    pickZoomableSectionInViewport,
    getNeighborZoomableSectionIds,
  ])

  const zoomRevealSeatViewIdsExpanded = useMemo(
    () =>
      expandWcSeamPairSectionIds(
        zoomRevealSeatViewZoomableSectionIds,
        data?.sections
      ),
    [data?.sections, zoomRevealSeatViewZoomableSectionIds]
  )

  // While lock is set (just clicked), use it; after pan clears lock, viewport drives
  // which section is “current” so neighbors/rows follow what you panned to.
  const neighborAnchorId =
    sectionCoversEnabled && seatsVisible
      ? lockedViewSectionId != null
        ? lockedViewSectionId
        : (viewportSectionId ??
          focusedSection?.sectionId ??
          clickedSectionId ??
          null)
      : null

  const neighborAnchorRect = neighborAnchorId
    ? zoomableSectionBoundsRef.current?.[String(neighborAnchorId)]
    : null

  const viewNeighborIds =
    neighborAnchorId && neighborAnchorRect
      ? getNeighborZoomableSectionIds(neighborAnchorId, neighborAnchorRect)
      : []

  const viewNeighborKey =
    viewNeighborIds.length > 0 ? [...viewNeighborIds].sort().join(',') : ''

  const seatViewZoomableSectionIds = useMemo(() => {
    if (neighborAnchorId == null || neighborAnchorId === '') return []
    const neighbors = viewNeighborKey
      ? viewNeighborKey.split(',').filter(Boolean)
      : []
    return Array.from(new Set([String(neighborAnchorId), ...neighbors]))
  }, [neighborAnchorId, viewNeighborKey])

  const seatViewZoomableSectionKey = useMemo(
    () => seatViewZoomableSectionIds.slice().sort().join(','),
    [seatViewZoomableSectionIds]
  )

  const seatViewZoomableSectionIdsExpanded = useMemo(
    () =>
      expandWcSeamPairSectionIds(seatViewZoomableSectionIds, data?.sections),
    [data?.sections, seatViewZoomableSectionIds]
  )

  const computedRowIds = useMemo(() => {
    if (!sectionCoversEnabled) return null
    if (seatsVisible) {
      if (seatViewZoomableSectionIdsExpanded.length === 0) return null
      return getRowsForZoomableSectionIds(seatViewZoomableSectionIdsExpanded)
    }
    if (zoomRevealSeats && zoomRevealSeatViewIdsExpanded.length > 0) {
      const rows = getRowsForZoomableSectionIds(zoomRevealSeatViewIdsExpanded)
      return rows.length > 0 ? rows : null
    }
    return null
  }, [
    sectionCoversEnabled,
    seatsVisible,
    seatViewZoomableSectionIdsExpanded,
    zoomRevealSeats,
    zoomRevealSeatViewIdsExpanded,
    getRowsForZoomableSectionIds,
  ])

  const seatViewIdsExpandedForLayers = useMemo(() => {
    if (seatsVisible) return seatViewZoomableSectionIdsExpanded
    if (zoomRevealSeats) return zoomRevealSeatViewIdsExpanded
    return []
  }, [
    seatsVisible,
    zoomRevealSeats,
    seatViewZoomableSectionIdsExpanded,
    zoomRevealSeatViewIdsExpanded,
  ])

  const sectionIdsWithSeatLayer = useMemo(() => {
    if (
      !Array.isArray(computedRowIds) ||
      computedRowIds.length === 0 ||
      !data?.rows
    ) {
      return null
    }
    const ids = new Set(seatViewIdsExpandedForLayers)
    for (const rowId of computedRowIds) {
      const sid = data.rows[rowId]?.sectionId
      if (sid != null && sid !== '') ids.add(String(sid))
    }
    return ids
  }, [computedRowIds, data?.rows, seatViewIdsExpandedForLayers])

  const uncoveredZoomableSectionIds = seatViewIdsExpandedForLayers

  useEffect(() => {
    if (!sectionCoversEnabled || !seatsVisible) {
      setViewportSectionId(null)
      return
    }
    let raf = null
    let lastTs = 0
    const tick = (ts) => {
      if (ts - lastTs > 66) {
        lastTs = ts
        const nextId = pickZoomableSectionInViewport()
        setViewportSectionId((prev) => (prev === nextId ? prev : nextId))
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => {
      if (raf) cancelAnimationFrame(raf)
    }
  }, [
    sectionCoversEnabled,
    focusedSection,
    pickZoomableSectionInViewport,
    seatsVisible,
  ])

  if (!loadingComplete) {
    return (
      <div className="d-flex align-items-center justify-content-center full-height full-width">
        <Spinner variant="primary" size="lg" />
      </div>
    )
  }

  if (mapError) {
    return (
      <div className="d-flex align-items-center justify-content-center full-height full-width">
        <span>Cannot display map...</span>
      </div>
    )
  }

  const { scale: vScale, posX, posY } = viewport
  const contentTransform = `translate(${posX}, ${posY}) scale(${vScale}) translate(${-offset.x}, ${-offset.y})`

  return (
    <div
      className="svg-seatmap-wrap flex-grow-1 d-flex flex-column"
      style={{ position: 'relative', minHeight: 0, width: '100%' }}
    >
      {sectionCoversEnabled && focusedSection ? (
        <div
          className="position-absolute top-0 start-0 m-2 d-flex flex-wrap gap-2 align-items-center"
          style={{ zIndex: 10 }}
        >
          <button
            type="button"
            className="btn btn-sm btn-light shadow-sm border"
            onClick={resetSectionFocus}
          >
            Back to map
          </button>
        </div>
      ) : null}

      <svg
        ref={svgRef}
        width={width || '100%'}
        height={height || '100%'}
        className="flex-grow-1"
        style={{
          display: 'block',
          touchAction: 'none',
          cursor: 'grab',
          minHeight: 0,
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {width > 0 && height > 0 && (
          <g transform={contentTransform}>
            {background?.url ? (
              <image
                href={background.url}
                width={originalWidth}
                height={originalHeight}
                preserveAspectRatio="none"
              />
            ) : null}
            <Sections
              data={data}
              sectionCoversEnabled={sectionCoversEnabled}
              showLabels={!seatsVisible}
              showCovers={seatsVisible}
              uncoveredZoomableSectionIds={uncoveredZoomableSectionIds}
              sectionIdsWithSeatLayer={sectionIdsWithSeatLayer}
              onZoomableSectionBounds={setZoomableSectionBounds}
              onSectionClick={onSectionClick}
            />
            <Seats
              data={data}
              sectionCoversEnabled={sectionCoversEnabled}
              activeRowIds={computedRowIds}
            />
          </g>
        )}
      </svg>
    </div>
  )
}
